import { css } from "@emotion/react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import {
  ChannelQueriesContext,
  ChannelQueriesContextProvider,
} from "@/pages/channels/pages/detail/channelQueriesContext";
import { ChannelNameEdit } from "@/pages/channels/pages/detail/components/ChannelNameEdit";
import { DisconnectModal } from "@/pages/channels/pages/detail/components/DisconnectModal";
import { ReauthorizeButton } from "@/pages/channels/pages/detail/components/ReauthorizeButton";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/channels/$type/$id");

const InstagramDetailPageInternal: React.FC = () => {
  const { t } = useTranslation();
  const { id } = route.useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [openDisconnectModal, toggleDisconnectModal] = useSwitch();

  const { channel } = ChannelQueriesContext.useData();
  const disconnectChannelMutation = interlude.channel.useUpdateChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess(data) {
        toggleDisconnectModal.off();
        enqueueSnackbar(
          t("page.channel.ig.disconnect.toast.success", {
            channelName: data.name,
          }),
          {
            variant: "success",
          },
        );
      },
    },
  );

  const handleChannelDisconnect = () => {
    disconnectChannelMutation.mutate({
      status: "disconnected",
    });
  };

  return (
    <>
      <Stack gap="8px">
        <Breadcrumbs aria-label="breadcrumb" separator=">">
          <Link to="/channels" css={css({ textDecoration: "none" })}>
            <Typography variant="body" color={theme.colors.staticFgBody}>
              {t("channels.title")}
            </Typography>
          </Link>
          <Link
            to="/channels/$type/$id"
            params={{
              type: "ig",
              id,
            }}
            css={css({ textDecoration: "none" })}
          >
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t("resource.channel.breadcrumb.ig.label")}
            </Typography>
          </Link>
        </Breadcrumbs>
        <PageTitle>{t("resource.channel.header.ig.label")}</PageTitle>
        <Stack gap="24px" css={css({ maxWidth: "512px" })}>
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("resource.channel.subHeader.ig.label")}
            </Typography>
            <ChannelNameEdit />
          </Stack>
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("resource.channel.update.credential.ig.header")}
            </Typography>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t("resource.channel.update.credential.expired.ig.description")}
            </Typography>
            {channel.status === "token-expired" ? (
              <InfoBox
                variant="error"
                title={t(
                  "resource.channel.update.credential.expired.ig.header",
                )}
                description={t(
                  "resource.channel.update.credential.expired.ig.description",
                )}
              />
            ) : null}
            <ReauthorizeButton
              variant="plain"
              disabled={channel.status === "disconnected"}
            >
              {t("resource.channel.update.credential.refresh.button.label")}
            </ReauthorizeButton>
          </Stack>
          <Stack gap="8px">
            <Typography
              variant="h3"
              fontWeight="medium"
              color={theme.colors.staticFgTitle}
            >
              {t("resource.channel.update.connection.header")}
            </Typography>
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t("resource.channel.update.connection.ig.description")}
            </Typography>
            {channel.status !== "disconnected" ? (
              <Button
                variant="plain"
                color="error"
                onClick={toggleDisconnectModal.on}
                loading={disconnectChannelMutation.isPending}
              >
                {t("common.disconnect")}
              </Button>
            ) : (
              <ReauthorizeButton variant="plain">
                {t("common.connect")}
              </ReauthorizeButton>
            )}
          </Stack>
        </Stack>
      </Stack>
      <DisconnectModal
        channelType="ig"
        open={openDisconnectModal}
        isLoading={disconnectChannelMutation.isPending}
        onClose={toggleDisconnectModal.off}
        onConfirm={handleChannelDisconnect}
      />
    </>
  );
};

export const InstagramDetailPage: React.FC = () => {
  return (
    <Suspense fallback={<BarLoading />}>
      <ChannelQueriesContextProvider>
        <InstagramDetailPageInternal />
      </ChannelQueriesContextProvider>
    </Suspense>
  );
};
